import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const ToastContext = createContext({ showToast: () => {} });

function toneStyles(type) {
  if (type === "success") {
    return {
      border: "border-emerald-200",
      bg: "bg-emerald-50",
      text: "text-emerald-800",
      dot: "bg-emerald-500",
    };
  }
  if (type === "error") {
    return {
      border: "border-red-200",
      bg: "bg-red-50",
      text: "text-red-800",
      dot: "bg-red-500",
    };
  }
  return {
    border: "border-slate-200",
    bg: "bg-white",
    text: "text-slate-800",
    dot: "bg-slate-400",
  };
}

function resolveToastAction(toast) {
  if (toast.actionLabel && typeof toast.onAction === "function") {
    return { label: toast.actionLabel, onAction: toast.onAction };
  }
  if (/Product added to grocery list/i.test(toast.message || "")) {
    return {
      label: "Go to Grocery List",
      onAction: () => {
        window.location.assign("/settings?tab=grocery&view=lists");
      },
    };
  }
  if (/No grocery list found/i.test(toast.message || "")) {
    return {
      label: "Create list",
      onAction: () => {
        window.location.assign("/settings?tab=grocery&view=create");
      },
    };
  }
  return null;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    ({ type = "info", title, message, duration = 3200, actionLabel = "", onAction = null }) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setToasts((prev) => [
        ...prev,
        {
          id,
          type,
          title: title || "",
          message: message || "",
          actionLabel: actionLabel || "",
          onAction: typeof onAction === "function" ? onAction : null,
        },
      ]);
      window.setTimeout(() => removeToast(id), duration);
    },
    [removeToast]
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-[70] flex w-full max-w-sm flex-col gap-2">
        {toasts.map((toast) => {
          const styles = toneStyles(toast.type);
          const action = resolveToastAction(toast);
          return (
            <div
              key={toast.id}
              className={`pointer-events-auto rounded-xl border ${styles.border} ${styles.bg} p-3 shadow-lg`}
              role="status"
              aria-live="polite"
            >
              <div className="flex items-start gap-2">
                <span className={`mt-1 h-2.5 w-2.5 rounded-full ${styles.dot}`} />
                <div className="min-w-0 flex-1">
                  {!!toast.title && (
                    <p className={`text-sm font-semibold ${styles.text}`}>{toast.title}</p>
                  )}
                  {!!toast.message && (
                    <p className={`text-xs ${styles.text} opacity-90 whitespace-pre-line`}>
                      {toast.message}
                    </p>
                  )}
                  {!!action && (
                    <button
                      type="button"
                      className="mt-2 rounded-md bg-[#7352C7] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#6546c3]"
                      onClick={() => {
                        action.onAction?.();
                        removeToast(toast.id);
                      }}
                    >
                      {action.label}
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeToast(toast.id)}
                  className="rounded p-0.5 text-slate-500 hover:bg-black/5"
                  aria-label="Close notification"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
